// HdCallout — the canonical Helpdesk inline notice.
//
// Translated from docs/design_handoff_helpdesk/prototype/primitives.jsx
// `Callout` (lines 362-380). Every number below traces to that prototype;
// colours are read as CSS custom properties from the .helpdesk-shell block,
// never hard-coded.
//
// ONE DELIBERATE DIVERGENCE, documented rather than silent: the prototype's
// `tx` (body-text) colour for warn/success/danger is an ad-hoc hex value
// baked into the mock (#7c4210 / #1f5232 / #7f2222) that does not correspond
// to anything in tokens.css — info/neutral already use `var(--ink-2)` for
// body text, so this translation uses `var(--ink-2)` for ALL tones' body
// text rather than introducing three new hex literals with no token behind
// them. The tone-coloured `fg` (icon + title) is unchanged from the
// prototype and still traces exactly.
//
// Presentational only — no dismiss button, no auto-hide. Wrap it where the
// caller needs those.
import React from 'react';
import { Box } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

export type HdCalloutTone = 'info' | 'warn' | 'success' | 'danger' | 'neutral';

// primitives.jsx lines 363-369 — bg/fg per tone, plus the default icon per
// tone (icon prop name in the prototype: 'info' | 'alert' | 'checkCircle' |
// 'shield' | 'info').
const TONES: Record<HdCalloutTone, { bg: string; fg: string; Icon: SvgIconComponent }> = {
  info: { bg: 'var(--accent-soft)', fg: 'var(--accent)', Icon: InfoOutlinedIcon },
  warn: { bg: 'var(--status-pending-bg)', fg: 'var(--status-pending-fg)', Icon: WarningAmberOutlinedIcon },
  success: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-fg)', Icon: CheckCircleOutlineIcon },
  danger: { bg: 'var(--status-danger-bg)', fg: 'var(--status-danger-fg)', Icon: ShieldOutlinedIcon },
  neutral: { bg: 'var(--card-sub)', fg: 'var(--muted)', Icon: InfoOutlinedIcon }
};

export interface HdCalloutProps {
  /** Defaults to 'info', matching the prototype's default. */
  tone?: HdCalloutTone;
  /** Overrides the tone's default icon. Pass `false` to render no icon. */
  icon?: SvgIconComponent | false;
  /** primitives.jsx `title` — bold, tone-coloured, above the body. Optional. */
  title?: React.ReactNode;
  children: React.ReactNode;
  sx?: React.ComponentProps<typeof Box>['sx'];
}

export default function HdCallout({ tone = 'info', icon, title, children, sx }: HdCalloutProps) {
  const t = TONES[tone];
  const Icon = icon === false ? null : icon || t.Icon;

  return (
    <Box
      sx={{
        // Layout — primitives.jsx lines 372.
        display: 'flex',
        gap: '9px',
        padding: '10px 12px',
        borderRadius: 'var(--r-sm)',
        backgroundColor: t.bg,
        ...sx
      }}
    >
      {Icon ? (
        <Icon sx={{ fontSize: 15, color: t.fg, marginTop: '2px', flexShrink: 0 }} />
      ) : null}
      <Box sx={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        {title ? (
          <Box sx={{ fontWeight: 600, color: t.fg, marginBottom: '2px' }}>{title}</Box>
        ) : null}
        {children}
      </Box>
    </Box>
  );
}
